import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  usePermissoes,
  CHAVES_PARCEIROS_CREATE,
  CHAVES_PARCEIROS_EDIT,
  CHAVES_PARCEIROS_DELETE,
} from "../../config/Context/PermissoesContext";
import {
  listarAssociados,
  listarParceiros,
  desativarAssociado,
  desativarParceiro,
} from "../../services/api-associados-parceiros.service";
import styles from "./stylesLista.module.css";
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import Pagination from "../../components/Pagination";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

const getExpirationClass = (expirationDate) => {
  const today = new Date();
  const expDate = new Date(expirationDate);
  const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays <= 30) return "warning";
  return "normal";
};

/** Mapeia associado da API para formato da tabela */
function mapAssociadoToRow(a) {
  return {
    id: a.id,
    entityType: "associado",
    documentType: (a.tipo_documento || "").toLowerCase(),
    document: a.documento_formatado || a.documento || "",
    name: a.razao_social,
    tipo: "associado",
    associado: null,
    responsavel: a.responsavel_comercial?.nome ?? "—",
    email: a.email,
    phone: a.telefone,
    expiration: a.data_expiracao,
    status: a.ativo ? "active" : "inactive",
  };
}

/** Mapeia parceiro da API para formato da tabela */
function mapParceiroToRow(p) {
  return {
    id: p.id,
    entityType: "parceiro",
    documentType: (p.tipo_documento || "").toLowerCase(),
    document: p.documento_formatado || p.documento || "",
    name: p.razao_social,
    tipo: "parceiro",
    associado: p.associado?.razao_social ?? "—",
    responsavel: p.responsavel_comercial?.nome ?? "—",
    email: p.email,
    phone: p.telefone,
    expiration: p.data_expiracao,
    status: p.ativo ? "active" : "inactive",
  };
}

export default function ParceirosAssociados() {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeCriar = pode(CHAVES_PARCEIROS_CREATE);
  const podeEditar = pode(CHAVES_PARCEIROS_EDIT);
  const podeInativar = pode(CHAVES_PARCEIROS_DELETE);
  const [tipoFilter, setTipoFilter] = useState("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [pageData, setPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawList, setRawList] = useState([]);
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const statusParam =
      statusFilter === "active" ? "ativo" : statusFilter === "inactive" ? "inativo" : "todos";
    const tipoDocParam =
      documentTypeFilter === "cpf" ? "CPF" : documentTypeFilter === "cnpj" ? "CNPJ" : undefined;
    const tipoAssocParam =
      tipoFilter === "associado" ? "ASSOCIADO" : tipoFilter === "parceiro" ? undefined : undefined;

    Promise.all([
      listarAssociados({
        page: 1,
        per_page: 500,
        status: statusParam,
        tipo_documento: tipoDocParam,
        tipo: tipoFilter === "parceiro" ? undefined : tipoAssocParam || undefined,
        search: searchTerm.trim() || undefined,
      }).then((res) => (res?.data || []).map(mapAssociadoToRow)),
      listarParceiros({
        page: 1,
        per_page: 500,
        status: statusParam,
        tipo_documento: tipoDocParam,
        associado_id: undefined,
        search: searchTerm.trim() || undefined,
      }).then((res) => (res?.data || []).map(mapParceiroToRow)),
    ])
      .then(([associados, parceiros]) => {
        if (cancelled) return;
        let merged = [...associados, ...parceiros];
        if (tipoFilter === "associado") merged = merged.filter((i) => i.entityType === "associado");
        if (tipoFilter === "parceiro") merged = merged.filter((i) => i.entityType === "parceiro");
        merged.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRawList(merged);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err?.message || "Erro ao carregar dados");
          setRawList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tipoFilter, documentTypeFilter, statusFilter, searchTerm]);

  const filteredData = useMemo(() => {
    return rawList.filter((item) => {
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const docTerm = searchTerm.replace(/\D/g, "");
        const match =
          (item.name || "").toLowerCase().includes(term) ||
          (docTerm.length > 0 && (item.document || "").replace(/\D/g, "").includes(docTerm));
        if (!match) return false;
      }
      return true;
    });
  }, [rawList, searchTerm]);

  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (sortField === "expiration") {
        const da = aVal ? new Date(aVal) : new Date(0);
        const db = bVal ? new Date(bVal) : new Date(0);
        return sortDir === "asc" ? da - db : db - da;
      }
      const cmp = String(aVal).localeCompare(String(bVal), "pt-BR", { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredData, sortField, sortDir]);

  const handleSort = (field) => {
    setSortDir(sortField === field ? (sortDir === "asc" ? "desc" : "asc") : "asc");
    setSortField(field);
  };

  const sortIcon = (field) => (
    <span style={{ fontSize: "10px", opacity: 0.3 }}>
      {sortField === field && sortDir === "desc" ? "▲" : "▼"}
    </span>
  );

  const stats = useMemo(() => {
    const total = filteredData.length;
    const activeCount = filteredData.filter((i) => i.status === "active").length;
    const inactiveCount = filteredData.filter((i) => i.status === "inactive").length;
    const today = new Date();
    const expiringCount = filteredData.filter((item) => {
      const diffDays = Math.ceil((new Date(item.expiration) - today) / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 30;
    }).length;
    return { total, activeCount, inactiveCount, expiringCount };
  }, [filteredData]);


  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) {
      setItemToDelete(null);
      setDeleteModalOpen(false);
      return;
    }
    const { entityType, id } = itemToDelete;
    try {
      if (entityType === "associado") await desativarAssociado(id);
      else await desativarParceiro(id);
      setRawList((prev) => prev.filter((i) => !(i.entityType === entityType && i.id === id)));
      setItemToDelete(null);
      setDeleteModalOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Erro ao desativar";
      alert(msg);
    }
  }, [itemToDelete]);

  const goToEdit = (item) => {
    navigate(`/acessos/parceiros-associados/${item.entityType}-${item.id}`);
  };

  const autocompleteSx = {
    minWidth: 160,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "& fieldset": { borderColor: "divider" },
    },
    "& .MuiInputLabel-root": { color: "#3c4043", fontSize: "0.875rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#3c4043" },
  };

  const tipoOptions = [
    { value: "all", label: "Todos os Tipos" },
    { value: "parceiro", label: "Parceiro" },
    { value: "associado", label: "Associado" },
  ];
  const docTypeOptions = [
    { value: "all", label: "Todos os Documentos" },
    { value: "cpf", label: "CPF" },
    { value: "cnpj", label: "CNPJ" },
  ];
  const statusOptions = [
    { value: "all", label: "Todos os Status" },
    { value: "active", label: "Ativo" },
    { value: "inactive", label: "Inativo" },
  ];

  const tipoValue = tipoOptions.find((o) => o.value === tipoFilter) || tipoOptions[0];
  const docTypeValue = docTypeOptions.find((o) => o.value === documentTypeFilter) || docTypeOptions[0];
  const statusValue = statusOptions.find((o) => o.value === statusFilter) || statusOptions[0];

  return (
    <>
      <Paper elevation={0} sx={{ margin: "0 2rem", borderRadius: "12px", bgcolor: "#f5f7fa", boxShadow: "0 1px 4px rgba(0,0,0,0.10)", overflow: "hidden" }}>
        <Box sx={{ margin: "16px 24px", borderRadius: "12px", border: "1px solid #e0e0e0", bgcolor: "#ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          {/* Header */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", padding: "20px 24px 16px", bgcolor: "#ffffff", borderBottom: "1px solid #e0e0e0" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: "auto" }}>
              <PeopleIcon sx={{ fontSize: 28, color: "#1b3a5f" }} />
              <Typography sx={{ fontSize: "1.375rem", fontWeight: 600, color: "#1b3a5f", whiteSpace: "nowrap" }}>Parceiros e Associados</Typography>
            </Box>
            <Autocomplete
              size="small"
              value={tipoValue}
              onChange={(_, v) => { setTipoFilter(v?.value ?? "all"); }}
              options={tipoOptions}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => <TextField {...params} label="Tipo" />}
              sx={autocompleteSx}
            />
            <Autocomplete
              size="small"
              value={docTypeValue}
              onChange={(_, v) => { setDocumentTypeFilter(v?.value ?? "all"); }}
              options={docTypeOptions}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => <TextField {...params} label="Documento" />}
              sx={autocompleteSx}
            />
            <Autocomplete
              size="small"
              value={statusValue}
              onChange={(_, v) => { setStatusFilter(v?.value ?? "all"); }}
              options={statusOptions}
              getOptionLabel={(o) => o?.label ?? ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              renderInput={(params) => <TextField {...params} label="Status" />}
              sx={autocompleteSx}
            />
            <TextField
              size="small"
              placeholder="Buscar por nome, documento..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); }}
              sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "8px" }, "& .MuiInputBase-input::placeholder": { color: "#3c4043", opacity: 1 } }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: "#3c4043" }} /></InputAdornment>) }}
            />
            {podeCriar && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate("/acessos/parceiros-associados/novo")}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, minHeight: 40, whiteSpace: "nowrap", px: 2, boxShadow: "none", "&:hover": { boxShadow: "none" }, flexShrink: 0 }}
              >
                Novo Cadastro
              </Button>
            )}
          </Box>

          {error && (
            <Box sx={{ padding: "12px 24px", bgcolor: "#fce8e6", color: "#c5221f", fontSize: "0.875rem" }} role="alert">
              {error}
            </Box>
          )}

          {/* Stats */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2, bgcolor: "#fafafa", padding: "20px 24px", "@media (max-width: 1024px)": { gridTemplateColumns: "repeat(2, 1fr)" }, "@media (max-width: 768px)": { gridTemplateColumns: "1fr" } }}>
            {[
              { icon: <PeopleIcon sx={{ fontSize: 24 }} />, bg: "#e3f2fd", color: "#1565c0", value: loading ? "—" : stats.total, label: "Total" },
              { icon: <CheckCircleIcon sx={{ fontSize: 24 }} />, bg: "#e8f5e9", color: "#2e7d32", value: loading ? "—" : stats.activeCount, label: "Ativos" },
              { icon: <CancelIcon sx={{ fontSize: 24 }} />, bg: "#fce8e6", color: "#c62828", value: loading ? "—" : stats.inactiveCount, label: "Inativos" },
              { icon: <HourglassBottomIcon sx={{ fontSize: 24 }} />, bg: "#fff3e0", color: "#e65100", value: loading ? "—" : stats.expiringCount, label: "Expirando em 30 dias" },
            ].map(({ icon, bg, color, value, label }) => (
              <Paper key={label} elevation={0} sx={{ py: 2, px: 2.5, display: "flex", alignItems: "center", gap: 2, borderRadius: "8px", border: "1px solid #e8ecf0", bgcolor: "#ffffff", boxShadow: "none", height: "100%", minHeight: 80, transition: "box-shadow 0.2s ease, border-color 0.2s ease", "&:hover": { borderColor: "#c5cdd8", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: bg, color, flexShrink: 0 }}>
                  {icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1, color: "#1b3a5f" }}>{value}</Typography>
                  <Typography sx={{ color: "#757575", fontSize: "0.75rem", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px", mt: 0.25 }}>{label}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
          <Box sx={{ height: "1px", bgcolor: "#e0e0e0" }} />

          {/* Table */}
          <Box sx={{ bgcolor: "#ffffff", overflowX: "auto" }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("document")}><div className={styles.thContent}>Documento {sortIcon("document")}</div></th>
                  <th onClick={() => handleSort("name")}><div className={styles.thContent}>Nome / Razão Social {sortIcon("name")}</div></th>
                  <th onClick={() => handleSort("tipo")}><div className={styles.thContent}>Tipo {sortIcon("tipo")}</div></th>
                  <th onClick={() => handleSort("associado")}><div className={styles.thContent}>Associado {sortIcon("associado")}</div></th>
                  <th onClick={() => handleSort("responsavel")}><div className={styles.thContent}>Responsável Comercial {sortIcon("responsavel")}</div></th>
                  <th onClick={() => handleSort("expiration")} style={{ textAlign: "center" }}><div className={styles.thContent} style={{ justifyContent: "center" }}>Expiração {sortIcon("expiration")}</div></th>
                  <th onClick={() => handleSort("status")}><div className={styles.thContent}>Status {sortIcon("status")}</div></th>
                  <th style={{ textAlign: "center", cursor: "default" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyRow}>Carregando...</td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={styles.emptyRow}>Nenhum registro encontrado</td>
                  </tr>
                ) : (
                  pageData.map((item) => {
                    const expClass = getExpirationClass(item.expiration);
                    return (
                      <tr key={`${item.entityType}-${item.id}`}>
                        <td>
                          <div className={styles.documentCell}>
                            <span className={`${styles.documentBadge} ${item.documentType === "cpf" ? styles.documentBadgeCpf : styles.documentBadgeCnpj}`}>
                              {(item.documentType || "cpf").toUpperCase()}
                            </span>
                            <span className={styles.documentNumber}>{item.document}</span>
                          </div>
                        </td>
                        <td className={styles.tdName} title={item.name}>{item.name}</td>
                        <td>
                          <span className={`${styles.typeBadge} ${item.tipo === "parceiro" ? styles.typeBadgeParceiro : styles.typeBadgeAssociado}`}>
                            {item.tipo === "parceiro" ? "Parceiro" : "Associado"}
                          </span>
                        </td>
                        <td className={styles.tdAssociado} title={item.associado ?? ""}>{item.associado ?? "—"}</td>
                        <td className={styles.tdResponsavel} title={item.responsavel ?? ""}>{item.responsavel}</td>
                        <td className={styles.tdExpiration}>
                          <span className={expClass === "expired" ? styles.expirationExpired : expClass === "warning" ? styles.expirationWarning : styles.expirationNormal}>
                            {formatDate(item.expiration)}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${item.status === "active" ? styles.statusBadgeActive : styles.statusBadgeInactive}`}>
                            <span className={styles.statusDot} />
                            {item.status === "active" ? "Ativo" : "Inativo"}
                          </span>
                        </td>
                        <td>
                          <div className={styles.actionsCell}>
                            {podeEditar && (
                              <div className={styles.tooltipWrapper}>
                                <button type="button" className={`${styles.actionBtn} ${styles.actionBtnEdit}`} onClick={() => goToEdit(item)} title="Editar">
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </button>
                                <span className={styles.tooltip}>Editar</span>
                              </div>
                            )}
                            {podeInativar && (
                              <div className={styles.tooltipWrapper}>
                                <button type="button" className={`${styles.actionBtn} ${styles.actionBtnDelete}`} onClick={() => { setItemToDelete(item); setDeleteModalOpen(true); }} title="Inativar">
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </button>
                                <span className={styles.tooltip}>Inativar</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          <Pagination
            paginationKey="parceiros-associados"
            data={sortedData}
            setFilteredData={setPageData}
          />
        </Box>
      </Paper>

      {deleteModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setDeleteModalOpen(false)} role="presentation">
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} role="dialog">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Desativar Registro</h3>
            </div>
            <div className={styles.modalBody}>
              {itemToDelete && (
                <>Tem certeza que deseja desativar &quot;{itemToDelete.name}&quot;? O registro não será exibido na listagem de ativos.</>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.btnSecondary} onClick={() => setDeleteModalOpen(false)}>Cancelar</button>
              <button type="button" className={styles.btnDanger} onClick={handleConfirmDelete}>Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
